import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ChevronDown } from 'react-bootstrap-icons';
import ChatMessage from './ChatMessage';

export default function MessageList({
  chatLog,
  isThinking,
  onRegenerate,
  onDropFiles,
}) {
  const scrollRef = useRef(null);
  const sentinelRef = useRef(null);
  const [stuckToBottom, setStuckToBottom] = useState(true);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setStuckToBottom(entry.isIntersecting),
      { root: scrollRef.current, threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (stuckToBottom && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [chatLog, isThinking, stuckToBottom]);

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, []);

  const lastAssistantId = [...chatLog]
    .reverse()
    .find((m) => m.role === 'assistant' || m.user === 'assistant')?.id;

  // The pending assistant placeholder already shows the thinking state, so a
  // standalone row here would duplicate it.
  const hasPendingAssistant = chatLog.some(
    (m) => m.streaming && (m.role === 'assistant' || m.user === 'assistant')
  );

  return (
    <div className="relative flex-1 min-h-0">
      <div
        ref={scrollRef}
        className="h-full overflow-y-auto"
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const files = Array.from(e.dataTransfer.files || []);
          if (files.length && onDropFiles) onDropFiles(files);
        }}
      >
        <div className="mx-auto w-full max-w-chat py-4 pb-8">
          {chatLog.map((message) => (
            <ChatMessage
              key={message.id || message.message}
              message={message}
              isLastAssistant={message.id === lastAssistantId}
              onRegenerate={onRegenerate}
            />
          ))}
          {isThinking && !hasPendingAssistant && (
            <ChatMessage showThinking message={{ role: 'assistant' }} />
          )}
          <div ref={sentinelRef} className="h-1" />
        </div>
      </div>

      {dragOver && (
        <div className="pointer-events-none absolute inset-4 z-20 flex items-center justify-center rounded-2xl border-2 border-dashed border-accent bg-accent/10 text-sm font-medium text-accent">
          Drop files to attach
        </div>
      )}

      {!stuckToBottom && (
        <button
          type="button"
          onClick={scrollToBottom}
          className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 items-center justify-center rounded-full border border-border-subtle bg-surface-elevated p-2 text-text-primary shadow-composer hover:bg-surface-hover"
          aria-label="Scroll to bottom"
        >
          <ChevronDown size={18} />
        </button>
      )}
    </div>
  );
}
